import React from 'react';
import { Phone, MessageCircle, Star, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../mockData';

const Hero = () => {
  const handleCall = () => {
    window.location.href = `tel:${businessInfo.phone}`;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${businessInfo.whatsapp}?text=Hi, I'm interested in your marble polishing services`, '_blank');
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section data-testid="hero-section" className="relative bg-gradient-to-br from-emerald-50 to-white py-16 md:py-24 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100 rounded-full blur-3xl opacity-30 -z-10"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200 rounded-full blur-3xl opacity-20 -z-10"></div>

      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            {/* Trust Badge */}
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm border border-emerald-100">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-semibold text-gray-700">40 Years of Excellence</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Precision Cutting & Fixing of <span className="text-emerald-600">Italian Marble</span> & Granite
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Bangalore's best stone experts — 40 years of Italian marble & granite cutting, flawless fixing, and mirror polishing with guaranteed shine.
            </p>

            {/* Benefits List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                'Expert Craftsmen',
                'Premium Quality',
                'Affordable Rates',
                'Same-Day Service Available'
              ].map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                onClick={handleCall}
                size="lg"
                data-testid="hero-call-button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: {businessInfo.phone}
              </Button>
              <Button 
                onClick={handleWhatsApp}
                size="lg"
                variant="outline"
                data-testid="hero-whatsapp-button"
                className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-lg px-8 py-6"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp Us
              </Button>
            </div>

            {/* Secondary CTA */}
            <button 
              onClick={scrollToContact}
              data-testid="hero-quote-link"
              className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors underline decoration-2 underline-offset-4"
            >
              Or Get a Free Quote →
            </button>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1758193017781-e3aee6c3e359?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxwb2xpc2hlZCUyMG1hcmJsZSUyMGZsb29yfGVufDB8fHx8MTc3NTAyMjc3NXww&ixlib=rb-4.1.0&q=85"
                alt="Polished Marble Floor"
                className="w-full h-[500px] object-cover"
              />
              {/* Overlay Badge */}
              <div className="absolute bottom-6 left-6 bg-white px-6 py-4 rounded-xl shadow-lg" data-testid="hero-customers-badge">
                <div className="flex items-center space-x-3">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-emerald-100 border-2 border-white flex items-center justify-center">
                        <Star className="w-5 h-5 text-emerald-600 fill-emerald-600" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">500+ Happy Customers</p>
                    <p className="text-sm text-gray-600">Across Bangalore</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
